/* @flow */
import Vue from 'vue'
import axios from 'axios'
import normalize from 'jsonapi-normalizer'

import {UPDATE_SESSION, REMOVE_SESSION} from './session'
import {NEW_NOTIFICATION, REPLACE_NOTIFICATION} from './notifications'
import {computePaginateSortFilterParams, computedCollectionCount} from '@/utils/api'

export const REPLACE_ORGANIZATIONS = 'REPLACE_ORGANIZATIONS'
export const ADD_ORGANIZATIONS = 'ADD_ORGANIZATIONS'
export const MODIFY_ORGANIZATION = 'MODIFY_ORGANIZATION'
export const DONE_ORGANIZATIONS_FETCHING = 'DONE_ORGANIZATIONS_FETCHING'
export const ADD_TO_PERSISTED_ITEMS = 'ADD_TO_PERSISTED_ITEMS'

export const START_ORGANIZATIONS_FETCHING = 'START_ORGANIZATIONS_FETCHING'
export const SET_ORGANIZATIONS_TOTAL = 'SET_ORGANIZATIONS_TOTAL'
export const FETCH_ORGANIZATIONS = 'FETCH_ORGANIZATIONS'
export const FETCH_ORGANIZATION = 'FETCH_ORGANIZATION'
export const UPDATE_ORGANIZATION = 'UPDATE_ORGANIZATION'
export const CREATE_ORGANIZATION = 'CREATE_ORGANIZATION'
export const REMOVE_ORGANIZATION = 'REMOVE_ORGANIZATION'
export const DELETE_ORGANIZATION = 'DELETE_ORGANIZATION'

const initialState = () => ({
  entities: {},
  persistedItems: {},
  statistics: {
    total: null,
    collectionCount: null,
  },
  fetch_status: {
    all: '',
  },
})

export default {
  state: initialState(),
  getters: {
    organizationDefaults: () => ({
      email: '',
    }),
    organizations: ({entities = {}}: Object) =>
      //$FlowFixMe
      Object.keys(entities).map(key => entities[key]),
    organizationsDropDown: ({entities = {}}: Object) => {
      Object.keys(entities)
        .map(key => entities[key])
        .map(org => {
          return {text: org.name, value: org.id}
        })
    },
    organizationById: (state: Object) => (id: string) => {
      return state.entities[id] || state.persistedItems[id] || {}
    },
  },
  mutations: {
    RESET_STATE(state: Object) {
      Object.assign(state, initialState())
    },
    [START_ORGANIZATIONS_FETCHING](state: Object, payload: Object) {
      state.fetch_status[payload.key] = 'loading'
    },
    [SET_ORGANIZATIONS_TOTAL](state: Object, payload: Object) {
      state.statistics = payload.meta.pagination
      state.statistics.total = payload.meta.pagination.count
    },
    [DONE_ORGANIZATIONS_FETCHING](state: Object, payload: Object) {
      state.fetch_status[payload.key] = 'loaded'
    },
    [REMOVE_SESSION](state: Object) {
      state.entities = initialState().entities
    },
    [REPLACE_ORGANIZATIONS](state: Object, {entities = {}}: Object) {
      state.entities = entities
    },
    [ADD_ORGANIZATIONS](state: Object, payload: Object) {
      state.entities = {
        ...state.entities,
        ...payload.entities,
      }
    },
    [MODIFY_ORGANIZATION](state: Object, payload: Object) {
      state.entities[payload.id] = {
        ...state.entities[payload.id],
        ...payload,
      }
    },
    [REMOVE_ORGANIZATION](state: Object, payload: Object) {
      Vue.delete(state.entities, payload.id)
    },
    [ADD_TO_PERSISTED_ITEMS](state: Object, payload: Object) {
      if (payload.id) state.persistedItems[payload.id] = payload
    },
  },
  actions: {
    [FETCH_ORGANIZATION]: async function({commit, dispatch}: Object, payload: Object) {
      try {
        const {data} = await axios.get(`/organizations/${payload.id}`)
        const links = data.data.links
        if (links){
          data.data.attributes.device_management_login_url = links.device_management_login_url
        }
        const {entities} = normalize(data)
        commit(ADD_ORGANIZATIONS, {
          entities: entities.organizations,
        })
        commit(ADD_TO_PERSISTED_ITEMS, data.data.attributes)
        return {success: true, organization: data.data.attributes}
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to load organization ',
          detail: err.message,
          color: 'error',
        })
        return {success: false}
      }
    },
    [FETCH_ORGANIZATIONS]: async function({commit, dispatch}: Object, payload: Object) {
      try {
        commit(START_ORGANIZATIONS_FETCHING, {key: 'all'})
        const {data} = await axios.get('/organizations', {
          params: computePaginateSortFilterParams(payload),
        })
        const {pagination} = data.meta
        commit(SET_ORGANIZATIONS_TOTAL, {total: data.data.length, meta: data.meta})

        commit(REPLACE_ORGANIZATIONS, {
          entities: normalize(data).entities.organizations
        })
        if (!payload){ payload = {} }
        return {
          success: true,
          items: data.data.map( (item) => {
            item.attributes.device_management_login_url=item.links
            return item.attributes
          }),
          collectionCount: computedCollectionCount({
            endRow: payload.endRow,
            filteredCount: pagination.filtered_count,
            collectionCount: pagination.collection_count,
          }),
        }
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to load organizations.',
          detail: err.message,
          color: 'error',
        })
        return {success: false}
      } finally {
        commit(DONE_ORGANIZATIONS_FETCHING, {key: 'all'})
      }
    },
    [UPDATE_ORGANIZATION]: async function({commit, dispatch}: Object, payload: Object) {
      const notificationId = await dispatch(NEW_NOTIFICATION, {
        text: 'Saving...',
        undoable: true,
        color: 'secondary',
      })

      try {
        const {data} = await axios.patch(`/organizations/${payload.id}`, {
          data: {
            type: 'organizations',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        commit(MODIFY_ORGANIZATION, entities.organizations[payload.id])
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Saved!',
          color: 'success',
          undoable: true,
        })
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Failed to update organization ',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [CREATE_ORGANIZATION]: async function({commit, dispatch}: Object, payload: Object) {
      const notificationId = await dispatch(NEW_NOTIFICATION, {
        text: 'Saving...',
        undoable: true,
        color: 'secondary',
      })

      try {
        const {data} = await axios.post(`/organizations`, {
          data: {
            type: 'organizations',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        commit(ADD_ORGANIZATIONS, {
          entities: entities.organizations,
        })
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Saved!',
          color: 'success',
          undoable: true,
        })
        return data.data.attributes.id
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Failed to create organization.',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [DELETE_ORGANIZATION]: async function({commit, dispatch}: Object, payload: Object) {
      const notificationId = await dispatch(NEW_NOTIFICATION, {
        text: 'Removing...',
        undoable: true,
        color: 'secondary',
      })

      try {
        const {data} = await axios.delete(`/organizations/${payload.id}`)
        commit(REMOVE_ORGANIZATION, payload)
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Organization has been successfully deleted!',
          color: 'success',
          undoable: true,
        })
        return data
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Failed to remove organization.',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
  },
}



// WEBPACK FOOTER //
// ./src/store/organizations.js